/**
 * Image upload rules for the `workshop-images` and `avatars` storage buckets
 * (ERI-2836). The buckets accept only these types, up to 10 MB, and refuse
 * anything else with a bare 400. Check files here before uploading so people
 * see why a photo was refused.
 *
 * Keep these in step with the bucket limits set in supabase/migrations
 * (20261030165530_harden_newsletter_storage_events.sql); a test checks it.
 */

export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/avif'] as const;

/** Extension → the MIME type storage expects for it. */
const EXTENSION_TYPES: Record<string, (typeof IMAGE_MIME_TYPES)[number]> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
};

/**
 * The `accept` value for every image upload input. Keep it narrow: when
 * `accept` doesn't list HEIC, iOS Safari converts HEIC to JPEG from the Photos
 * picker. `image/*` lets HEIC through, and the bucket refuses it.
 */
export const IMAGE_ACCEPT = [
  ...IMAGE_MIME_TYPES,
  ...Object.keys(EXTENSION_TYPES).map((ext) => `.${ext}`),
].join(',');

/** 10 MB, the bucket's file_size_limit. */
export const MAX_IMAGE_BYTES = 10485760;

export const UPLOAD_FAILED_MESSAGE = 'Failed to upload photo';

const USE_INSTEAD = 'Use JPEG, PNG, WebP, GIF or AVIF.';
const HEIC_MESSAGE = `HEIC photos aren't supported. ${USE_INSTEAD}`;
const SVG_MESSAGE = `SVG files aren't supported. ${USE_INSTEAD}`;
const UNSUPPORTED_TYPE_MESSAGE = `That file type isn't supported. ${USE_INSTEAD}`;
const OVER_LIMIT_MESSAGE = 'This photo is over the 10 MB limit.';

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot + 1).toLowerCase();
}

/** The type storage will see: the browser's, or one from the extension when the browser gave none. */
function effectiveType(file: File): string {
  if (file.type) return file.type.toLowerCase();
  return EXTENSION_TYPES[extensionOf(file.name)] ?? '';
}

/** The refusal message for a type the bucket doesn't take. `hint` is a MIME type, an extension or a storage message. */
function unsupportedTypeMessage(hint: string): string {
  if (/heic|heif/i.test(hint)) return HEIC_MESSAGE;
  if (/svg/i.test(hint)) return SVG_MESSAGE;
  return UNSUPPORTED_TYPE_MESSAGE;
}

function tooLargeMessage(bytes: number): string {
  return `This photo is ${(bytes / 1048576).toFixed(1)} MB. The limit is 10 MB.`;
}

/**
 * Check a picked file against the bucket rules. Returns the message to show,
 * or null when the file can be uploaded. When the browser gives no type, the
 * extension decides.
 */
export function validateImageFile(file: File): string | null {
  const type = effectiveType(file);
  if (!(IMAGE_MIME_TYPES as readonly string[]).includes(type)) {
    return unsupportedTypeMessage(file.type || extensionOf(file.name));
  }
  if (file.size > MAX_IMAGE_BYTES) return tooLargeMessage(file.size);
  return null;
}

/**
 * The file to hand to storage. A file with no type uploads as
 * application/octet-stream, which the bucket refuses, so give it the type its
 * extension implies. Call this only on a file validateImageFile passed.
 */
export function withImageType(file: File): File {
  if (file.type) return file;
  return new File([file], file.name, { type: effectiveType(file), lastModified: file.lastModified });
}

/**
 * The message for an upload error. Storage refuses a bad file with HTTP 400 and
 * a body like `{ statusCode: "415", message: "mime type image/heic is not
 * supported" }` or `{ statusCode: "413", message: "The object exceeded the
 * maximum allowed size" }`, and storage-js hands that body back as `error`.
 * Anything else gets the generic text.
 */
export function storageUploadMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return UPLOAD_FAILED_MESSAGE;
  const { statusCode, status, message } = error as { statusCode?: unknown; status?: unknown; message?: unknown };
  const code = String(statusCode ?? status ?? '');
  const text = typeof message === 'string' ? message : '';

  if (code === '415' || /mime type .* is not supported/i.test(text)) return unsupportedTypeMessage(text);
  if (code === '413' || /exceeded the maximum allowed size/i.test(text)) return OVER_LIMIT_MESSAGE;
  return UPLOAD_FAILED_MESSAGE;
}

/** Title and description for a single-photo upload error toast. */
export function uploadErrorToast(message: string): { title: string; description?: string } {
  return message === UPLOAD_FAILED_MESSAGE
    ? { title: UPLOAD_FAILED_MESSAGE }
    : { title: UPLOAD_FAILED_MESSAGE, description: message };
}

/** Title and description for the toast after a multi-photo pick skipped some files. */
export function skippedPhotosToast(messages: string[]): { title: string; description: string } {
  const count = messages.length;
  const reasons = Array.from(new Set(messages)).map((m) => (m.endsWith('.') ? m : `${m}.`));
  return {
    title: `Skipped ${count} ${count === 1 ? 'photo' : 'photos'}`,
    description: reasons.join(' '),
  };
}
